import type { ShopperProducts } from '@/scapi';
import { getCategoryDisplayName } from '@/lib/category-display-name';
import { isLeafCategory } from '@/lib/category-link-target';
import { isCategoryWithoutProducts } from '@/lib/category-product-counts';
import { createCategoryUrl } from '@/lib/category-url';

/**
 * SEO "Potrebbero interessarti" link list.
 */

export type SeoLink = {
    id: string;
    name: string;
    to: string;
};

type CategoryWithId = ShopperProducts.schemas['Category'] & { id: string };

function toValidLinks(
    nodes: ShopperProducts.schemas['Category'][] | undefined,
    refineId: string | undefined,
    productCountById?: Map<string, number>
): SeoLink[] {
    if (!nodes?.length) {
        return [];
    }
    return nodes
        .filter((c): c is CategoryWithId => Boolean(c?.id))
        .filter((c) => c.id !== refineId)
        .filter((c) => !isCategoryWithoutProducts(productCountById, c.id))
        .map((c) => {
            const name = getCategoryDisplayName(c);
            return {
                id: c.id,
                name,
                to: createCategoryUrl(c),
            };
        })
        .filter((link) => Boolean(link.name));
}

/**
 * Candidate categories for PLP subcategory tags / SEO links (before product-presence filtering).
 * - Non-leaf: direct children of the current category.
 * - Leaf: siblings (other children of the same parent); requires `parentForSiblings`
 *   from a separate getCategory(parentCategoryId, levels: 1) call.
 */
export function resolveLinkCandidateCategories({
    current,
    parentForSiblings,
    refineId,
}: {
    current: ShopperProducts.schemas['Category'];
    parentForSiblings?: ShopperProducts.schemas['Category'] | null;
    refineId?: string;
}): CategoryWithId[] | null {
    if (!isLeafCategory(current)) {
        const children = (current.categories ?? []).filter((c): c is CategoryWithId => Boolean(c?.id));
        const filtered = refineId ? children.filter((c) => c.id !== refineId) : children;
        return filtered.length > 0 ? filtered : null;
    }

    if (!parentForSiblings?.categories?.length) {
        return null;
    }

    const siblings = parentForSiblings.categories
        .filter((c): c is CategoryWithId => Boolean(c?.id && c.id !== current.id))
        .filter((c) => c.id !== refineId);
    return siblings.length > 0 ? siblings : null;
}

/**
 * Resolves which category links to show for "Potrebbero interessarti".
 * - Non-leaf: direct children of the current category.
 * - Leaf: siblings (other children of the same parent); requires `parentForSiblings`
 *   from a separate getCategory(parentCategoryId, levels: 1) call.
 * Returns `null` when the section should be suppressed.
 * Categories recorded with `productCount === 0` are omitted. Unknown ids and a missing map stay visible.
 */
export function resolveLinkTargets({
    current,
    parentForSiblings,
    refineId,
    productCountById,
}: {
    current: ShopperProducts.schemas['Category'];
    parentForSiblings?: ShopperProducts.schemas['Category'] | null;
    refineId?: string;
    productCountById?: Map<string, number>;
}): SeoLink[] | null {
    if (!isLeafCategory(current)) {
        const links = toValidLinks(current.categories, refineId, productCountById);
        return links.length > 0 ? links : null;
    }

    if (!parentForSiblings?.categories?.length) {
        return null;
    }

    const links = toValidLinks(
        parentForSiblings.categories.filter((c) => c.id && c.id !== current.id),
        refineId,
        productCountById
    );
    return links.length > 0 ? links : null;
}
